// Microbe RTS — Canvas 2D renderer.
//
// mountRender(canvas, sim) returns a pure `render(alpha)` (no sim mutation) plus the camera it
// owns. The renderer READS sim.state only. Draw order, back to front:
//   1. depth-gradient water (lit surface → dark deep → toxin-tinted abyss, via SUN/TOXIN_DPS)
//   2. surface light shafts + abyssal toxin haze (world space, so they scroll with the map)
//   3. nodes  — shape+glyph per type, ownership ring, integrity arc, claim/capture ring,
//               contested pulse, Domination ring when a side is banking a hold
//   4. units  — a distinct SILHOUETTE + role glyph per class, side color, hp pip, selection ring,
//               and a focus reticle on any enemy a selected unit is focus-firing
//   5. Great Oxygenation O2 wash when clocks.oxyActive
//   6. minimap ribbon
//
// Colorblind-safe: every faction/type distinction carries a SHAPE or GLYPH, never color alone.
// Handles devicePixelRatio + resize. All balance numbers come from config; colors/line-widths local.

import { WORLD, SUN, TOXIN_DPS, CAPTURE, WIN, BASE_DEFENSE as DEF, TURRET } from '../config.js';
import type { Sim, WorldNode, Unit, Side, Alliance, Turret } from '../types.js';
import { createCamera } from './camera.js';
import type { Camera } from './camera.js';
import { drawMinimap } from './minimap.js';
import type { MinimapRect } from './minimap.js';
import { createFog } from './fog.js';
import type { Fog } from './fog.js';
import { drawNodeIcon, drawUnitIcon } from './icons.js';

const TAU = Math.PI * 2;
// Approximate sim fixed step; used only to extrapolate unit motion by `alpha` for smooth draw.
const RENDER_STEP = 1 / 30;

const SIDE_COLOR: Record<Side, string> = { you: '#4fd1b0', bot: '#ff6a5a' };
const SIDE_DARK: Record<Side, string> = { you: '#1f6b5c', bot: '#8f2f28' };
const WILD_COLOR = '#d0a83f'; // amber — the neutral fauna alliance
const WILD_DARK = '#6f5518';
const NEUTRAL = '#8a97a3';

/** Alliance-aware colors (adds the wild fauna to the two colonies). */
function allyColor(side: Alliance): string {
  return side === 'wild' ? WILD_COLOR : SIDE_COLOR[side];
}
function allyDark(side: Alliance): string {
  return side === 'wild' ? WILD_DARK : SIDE_DARK[side];
}

export function mountRender(
  canvas: HTMLCanvasElement,
  sim: Sim,
  opts: { godView?: boolean; viewSide?: Side } = {},
): { render(alpha: number): void; cam: Camera; minimapHit: HTMLDivElement; enableGodView(): void; destroy(): void } {
  // The LOCAL player's side for presentation only (selection rings): solo 'you', PvP the session's
  // side. Never enters the sim or the hash — it just picks which side's selection this client draws.
  const viewSide: Side = opts.viewSide ?? 'you';
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) throw new Error('microbe-rts/render: 2D canvas context unavailable');
  const g: CanvasRenderingContext2D = ctx2d;

  // ---- minimap overlay canvas ----
  // The minimap MUST sit above the bottom control dock (DOM, inside #hud z-40) so its full height
  // is always visible — but the world/minimap share the #stage canvas (z-10), which the dock
  // covers. So the ribbon renders onto its OWN full-viewport transparent canvas stacked above the
  // HUD (z-60, below the toast/evolve/result modals) with pointer-events:none, so taps fall
  // straight through to the dock controls underneath.
  const overlay = document.createElement('canvas');
  overlay.setAttribute('aria-hidden', 'true');
  overlay.style.cssText =
    'position:fixed;inset:0;width:100%;height:100%;z-index:60;pointer-events:none;';
  document.body.appendChild(overlay);
  const octx = overlay.getContext('2d');
  if (!octx) throw new Error('microbe-rts/render: 2D overlay context unavailable');
  const og: CanvasRenderingContext2D = octx;

  // Interactive hit layer sized exactly to the ribbon (positioned in layoutMinimap). The overlay
  // CANVAS stays pointer-events:none so taps fall through to the dock; THIS thin div (z above the
  // canvas) captures scrub/tap on the ribbon only. ui/minimap-input.ts owns its pointer logic.
  // z-index 20 is deliberate: ABOVE the #stage map canvas (z-10) so the ribbon column captures its
  // own gestures, but BELOW #hud (z-40, holds the dock) so where the ribbon overlaps the bottom-left
  // dock chips, the CHIPS win — no stolen taps, no phantom move-orders. The full-height *visual*
  // ribbon lives on the separate overlay canvas (z-60), so it still paints over the dock.
  const minimapHit = document.createElement('div');
  minimapHit.setAttribute('aria-hidden', 'true');
  minimapHit.style.cssText = 'position:fixed;z-index:20;pointer-events:auto;touch-action:none;';
  document.body.appendChild(minimapHit);

  // The minimap STAYS visible while a node control panel is open (Ara) — but the ribbon overlay canvas
  // is body-level z60 and the panel lives inside #hud (its own z40 stacking context), so the panel can
  // NEVER paint over the ribbon no matter its z-index. So instead of letting the ribbon cover the node
  // controls, we SHRINK it vertically to end just above the panel's top edge (layoutMinimap measures the
  // live dock rect while open). The whole map stays in view, the controls are never blocked.
  const minimapVisible = true;
  let panelOpen = false;
  let dockEl: HTMLElement | null = null;
  const onPanelEvt = (e: Event): void => {
    panelOpen = !!(e as CustomEvent<{ open: boolean }>).detail?.open;
  };
  window.addEventListener('microbe:panel', onPanelEvt);

  const cam = createCamera(canvas);

  // ---- transient order pings — a brief marker at a tapped order point, fed by ui/input.ts.
  //      'move' = teal expanding ring, 'attack'/focus = red contracting reticle. Drawn in SCREEN
  //      space (crisp regardless of zoom); off-screen targets simply draw off-canvas. ----
  interface Ping { x: number; y: number; kind: 'move' | 'attack'; born: number; }
  const pings: Ping[] = [];
  const PING_MS = 640;
  const onPingEvt = (e: Event): void => {
    const d = (e as CustomEvent<{ x: number; y: number; kind: 'move' | 'attack' }>).detail;
    if (!d) return;
    pings.push({ x: d.x, y: d.y, kind: d.kind, born: performance.now() });
    if (pings.length > 24) pings.splice(0, pings.length - 24);
  };
  window.addEventListener('microbe:ping', onPingEvt);
  function drawPings(now: number): void {
    for (let i = pings.length - 1; i >= 0; i--) {
      const p = pings[i];
      const age = (now - p.born) / PING_MS;
      if (age >= 1) { pings.splice(i, 1); continue; }
      const s = cam.worldToScreen({ x: p.x, y: p.y });
      const fade = 1 - age;
      g.save();
      g.lineCap = 'round';
      if (p.kind === 'move') {
        const rr = 10 + age * 26;
        g.strokeStyle = '#7fe8d4';
        g.lineWidth = 3;
        g.globalAlpha = 0.85 * fade;
        g.beginPath(); g.arc(s.x, s.y, rr, 0, TAU); g.stroke();
        g.globalAlpha = 0.4 * fade;
        g.beginPath(); g.arc(s.x, s.y, rr * 0.55, 0, TAU); g.stroke();
      } else {
        const rr = 34 - age * 18;
        const tk = 6;
        g.strokeStyle = '#ff6a5a';
        g.lineWidth = 3;
        g.globalAlpha = 0.92 * fade;
        g.beginPath(); g.arc(s.x, s.y, rr, 0, TAU); g.stroke();
        g.beginPath();
        g.moveTo(s.x - rr - tk, s.y); g.lineTo(s.x - rr + tk, s.y);
        g.moveTo(s.x + rr - tk, s.y); g.lineTo(s.x + rr + tk, s.y);
        g.moveTo(s.x, s.y - rr - tk); g.lineTo(s.x, s.y - rr + tk);
        g.moveTo(s.x, s.y + rr - tk); g.lineTo(s.x, s.y + rr + tk);
        g.stroke();
      }
      g.restore();
    }
  }

  let W = 0;
  let H = 0;
  let dpr = 1;
  let safeTop = 0; // env(safe-area-inset-top) in CSS px — bigger on Dynamic-Island phones
  let last = performance.now();

  // Hidden probe so the minimap ribbon can clear the top-left back button + glyph pill, whose
  // offsets track the safe-area-top inset (≈47px notch, ≈59px Dynamic Island). Measured, not
  // assumed, because a hard-coded top would get clipped by those elements on tall-inset devices.
  const satProbe = document.createElement('div');
  satProbe.style.cssText =
    'position:fixed;top:0;left:0;width:0;height:env(safe-area-inset-top);visibility:hidden;pointer-events:none;';
  document.body.appendChild(satProbe);

  let destroyed = false;
  function resize(): void {
    if (destroyed) return; // a late resize event after teardown must not touch removed canvases
    dpr = Math.min(window.devicePixelRatio || 1, 3);
    W = canvas.clientWidth || window.innerWidth || 1;
    H = canvas.clientHeight || window.innerHeight || 1;
    safeTop = satProbe.getBoundingClientRect().height || 0;
    canvas.width = Math.round(W * dpr);
    canvas.height = Math.round(H * dpr);
    overlay.width = canvas.width;
    overlay.height = canvas.height;
    cam.resize(W, H);
  }
  resize();
  window.addEventListener('resize', resize);
  // iOS standalone-PWA gotcha: the viewport height often settles a beat AFTER first paint, so a
  // canvas sized on boot can fall short and leave a dead strip along the bottom edge that never
  // clears (no 'resize' fires). Re-measure on the visualViewport, on orientation, and once more
  // after load / on the next frames so the canvas always bleeds to the true bottom.
  window.visualViewport?.addEventListener('resize', resize);
  const onOrient = (): void => {
    resize();
    window.setTimeout(resize, 250);
  };
  window.addEventListener('orientationchange', onOrient);
  window.addEventListener('load', resize);
  requestAnimationFrame(() => requestAnimationFrame(resize));

  // Fog of war — persistent explored grid + soft veil. Rebuilt from sim.state each frame.
  // GOD VIEW (sandbox): swap in an all-revealing shim satisfying the Fog interface — every cull
  // gate (main view + minimap consume the SAME fog object) then passes for both sides, and the veil
  // no-ops. Both sides still draw in their own SIDE_COLOR; health arcs/pips are ungated already.
  const REVEAL_ALL: Fog = { update() {}, isVisible: () => true, isExplored: () => true, paint() {}, drawVeil() {} };
  let fog: Fog = opts.godView ? REVEAL_ALL : createFog(viewSide);

  // Flip the live view into no-fog whole-map god mode after mount (used by the post-game "Watch in
  // god mode" review — the finished sim stays in memory, so this just reveals it and frames it).
  function enableGodView(): void {
    fog = REVEAL_ALL;
    cam.jumpTo({ x: WORLD.width / 2, y: WORLD.height / 2 });
    cam.zoom = 0; // clamped to CAMERA.zoomMin -> whole-map overview
  }

  if (opts.godView) {
    // Frame the whole battlefield; pinch still works.
    cam.jumpTo({ x: WORLD.width / 2, y: WORLD.height / 2 });
    cam.zoom = 0; // clamped to CAMERA.zoomMin -> whole-map overview
  } else {
    // Frame the player's Home on first mount — or, before a home is colonized, the dropped squad.
    const homeId = sim.state.colonies[viewSide].homeId;
    const home = sim.nodeById(homeId);
    if (home) {
      cam.jumpTo(home.pos);
    } else {
      let cx = 0;
      let cy = 0;
      let n = 0;
      for (const u of sim.state.units) {
        if (u.side === viewSide && u.hp > 0) { cx += u.pos.x; cy += u.pos.y; n++; }
      }
      if (n > 0) cam.jumpTo({ x: cx / n, y: cy / n });
    }
  }

  // ------------------------------------------------------------------ water
  function waterColor(depth: number): string {
    const s = SUN(depth); // 1 at surface → 0 by d≈0.6
    const tox = Math.min(1, TOXIN_DPS(depth) / 6); // 0 above 0.55 → 1 at floor
    const r = Math.round(6 + 24 * s + 26 * tox);
    const gg = Math.round(16 + 76 * s + 4 * tox);
    const b = Math.round(24 + 74 * s + 30 * tox);
    return `rgb(${r},${gg},${b})`;
  }
  function drawWater(): void {
    const topW = cam.screenToWorld({ x: 0, y: 0 }).y;
    const botW = cam.screenToWorld({ x: 0, y: H }).y;
    const grad = g.createLinearGradient(0, 0, 0, H);
    const STOPS = 10;
    for (let i = 0; i <= STOPS; i++) {
      const t = i / STOPS;
      const wy = topW + (botW - topW) * t;
      const depth = Math.max(0, Math.min(1, wy / WORLD.height));
      grad.addColorStop(t, waterColor(depth));
    }
    g.fillStyle = grad;
    g.fillRect(0, 0, W, H);
  }

  // Light shafts near the surface + toxin haze near the floor — drawn in WORLD space so they pan.
  function drawAtmosphere(): void {
    // shafts across the photic band
    const shaftBottom = WORLD.height * 0.26;
    const cols = 6;
    for (let i = 0; i < cols; i++) {
      const fx = (i + 0.5) / cols;
      const x = fx * WORLD.width;
      const wskew = WORLD.width * 0.02;
      const halfTop = WORLD.width * 0.03;
      const grad = g.createLinearGradient(x, 0, x, shaftBottom);
      grad.addColorStop(0, 'rgba(180,230,240,0.10)');
      grad.addColorStop(1, 'rgba(180,230,240,0)');
      g.fillStyle = grad;
      g.beginPath();
      g.moveTo(x - halfTop, 0);
      g.lineTo(x + halfTop, 0);
      g.lineTo(x + halfTop + wskew, shaftBottom);
      g.lineTo(x - halfTop + wskew, shaftBottom);
      g.closePath();
      g.fill();
    }
    // toxin haze from d=0.55 to the floor
    const hazeTop = WORLD.height * 0.55;
    const haze = g.createLinearGradient(0, hazeTop, 0, WORLD.height);
    haze.addColorStop(0, 'rgba(150,60,190,0)');
    haze.addColorStop(1, 'rgba(150,60,190,0.28)');
    g.fillStyle = haze;
    g.fillRect(0, hazeTop, WORLD.width, WORLD.height - hazeTop);
  }

  // ------------------------------------------------------------------ nodes
  function nodeBodyShape(n: WorldNode, r: number): void {
    g.beginPath();
    switch (n.type) {
      case 'home': {
        for (let i = 0; i < 6; i++) {
          const a = -Math.PI / 2 + (i / 6) * TAU;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
      case 'reef': {
        // clustered lobes
        for (let k = 0; k < 3; k++) {
          const a = -Math.PI / 2 + (k / 3) * TAU;
          const cx = Math.cos(a) * r * 0.4;
          const cy = Math.sin(a) * r * 0.4;
          g.moveTo(cx + r * 0.7, cy);
          g.arc(cx, cy, r * 0.7, 0, TAU);
        }
        break;
      }
      case 'vent': {
        // chimney triangle
        g.moveTo(0, -r);
        g.lineTo(r * 0.85, r * 0.7);
        g.lineTo(-r * 0.85, r * 0.7);
        g.closePath();
        break;
      }
      case 'light': {
        // 8-point sun
        const pts = 8;
        for (let k = 0; k < pts * 2; k++) {
          const rr = k % 2 === 0 ? r : r * 0.5;
          const a = -Math.PI / 2 + (Math.PI * k) / pts;
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
      case 'toxin': {
        // spiky hazard
        const pts = 6;
        for (let k = 0; k < pts * 2; k++) {
          const rr = k % 2 === 0 ? r : r * 0.55;
          const a = -Math.PI / 2 + (Math.PI * k) / pts;
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
    }
  }

  function ownerColor(owner: WorldNode['owner']): string {
    return owner === 'neutral' ? NEUTRAL : SIDE_COLOR[owner];
  }

  function domActive(side: Side): boolean {
    // A side is "banking" a Domination hold when its countdown has dropped below the max.
    const c = sim.state.clocks;
    const t = side === 'you' ? c.domYou : c.domBot;
    return t < WIN.dominationHoldMax - 0.01;
  }

  function drawNode(n: WorldNode, time: number): void {
    const p = n.pos;
    const ring = n.radius;
    const bodyR = Math.min(ring * 0.5, n.type === 'home' ? 46 : 34);
    const col = ownerColor(n.owner);
    g.save();
    g.translate(p.x, p.y);

    // capture/interaction radius (ownership ring)
    g.lineWidth = 3;
    g.strokeStyle = col;
    g.globalAlpha = n.owner === 'neutral' ? 0.5 : 0.9;
    g.beginPath();
    g.arc(0, 0, ring, 0, TAU);
    g.stroke();
    g.globalAlpha = 1;

    // Core hitbox — the "structure" a warrior must beat down (combat + establish key off THIS,
    // not the big ooze ring). Faint inner ring; flares red while under warrior assault.
    const core = ring * CAPTURE.coreRadiusFrac;
    g.lineWidth = n.contested ? 2.5 : 1.5;
    g.strokeStyle = n.contested
      ? `rgba(255,90,80,${0.5 + 0.4 * Math.sin(time * 8)})`
      : 'rgba(255,255,255,0.14)';
    g.setLineDash([4, 6]);
    g.beginPath();
    g.arc(0, 0, core, 0, TAU);
    g.stroke();
    g.setLineDash([]);

    // Base-defense ooze — a faint poison fill inside owned nodes' radius (the contact hazard).
    if (n.owner !== 'neutral') {
      const oozePulse = 0.5 + 0.5 * Math.sin(time * 2 + p.x * 0.01);
      const oozeR = ring * 0.9;
      const oz = g.createRadialGradient(0, 0, ring * 0.2, 0, 0, oozeR);
      const strong = n.type === 'home';
      const a = (strong ? 0.16 : 0.1) + 0.05 * oozePulse;
      oz.addColorStop(0, `rgba(120,220,110,${a})`);
      oz.addColorStop(1, 'rgba(120,220,110,0)');
      g.fillStyle = oz;
      g.beginPath();
      g.arc(0, 0, oozeR, 0, TAU);
      g.fill();

      // Tier-2 ranged defense: a thin dashed firing-range ring.
      const tier = sim.state.colonies[n.owner].tier;
      if (tier >= 2) {
        const range = n.type === 'home' ? DEF.homeRange : DEF.colonyRange;
        g.save();
        g.strokeStyle = col;
        g.globalAlpha = 0.18;
        g.lineWidth = 1.5;
        g.setLineDash([10, 12]);
        g.beginPath();
        g.arc(0, 0, range, 0, TAU);
        g.stroke();
        g.setLineDash([]);
        g.restore();
      }
    }

    // Domination ring — pulsing outer halo on owned nodes while that side is banking a hold
    if (n.owner !== 'neutral' && domActive(n.owner)) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 4);
      g.lineWidth = 2 + 2 * pulse;
      g.strokeStyle = col;
      g.globalAlpha = 0.35 + 0.4 * pulse;
      g.beginPath();
      g.arc(0, 0, ring + 10, 0, TAU);
      g.stroke();
      g.globalAlpha = 1;
    }

    // contested — both sides present, capture frozen: red warning ring
    if (n.contested) {
      const pulse = 0.5 + 0.5 * Math.sin(time * 8);
      g.lineWidth = 2.5;
      g.strokeStyle = `rgba(255,90,80,${0.4 + 0.5 * pulse})`;
      g.beginPath();
      g.arc(0, 0, ring - 4, 0, TAU);
      g.stroke();
    }

    // body fill + outline
    nodeBodyShape(n, bodyR);
    const grad = g.createRadialGradient(0, 0, bodyR * 0.2, 0, 0, bodyR);
    grad.addColorStop(0, 'rgba(20,30,36,0.9)');
    grad.addColorStop(1, n.owner === 'neutral' ? 'rgba(40,48,54,0.85)' : SIDE_DARK[n.owner]);
    g.fillStyle = grad;
    g.fill();
    g.lineWidth = 2;
    g.strokeStyle = col;
    g.stroke();

    // type icon (custom canvas pictograph — no dependency on the platform glyph font)
    drawNodeIcon(g, n.type, bodyR * 0.82, 'rgba(255,255,255,0.92)');

    // integrity arc (top): how intact the node is
    const intFrac = n.maxIntegrity > 0 ? Math.max(0, Math.min(1, n.integrity / n.maxIntegrity)) : 1;
    if (intFrac < 0.999 || n.type === 'home') {
      const ar = bodyR + 6;
      g.lineWidth = 3;
      g.strokeStyle = 'rgba(255,255,255,0.12)';
      g.beginPath();
      g.arc(0, 0, ar, 0, TAU);
      g.stroke();
      g.strokeStyle = intFrac > 0.5 ? col : intFrac > 0.25 ? '#ffcf5c' : '#ff5a5a';
      g.beginPath();
      g.arc(0, 0, ar, -Math.PI / 2, -Math.PI / 2 + intFrac * TAU);
      g.stroke();
    }

    // seed ring — a lone drone channeling a colony on a NEUTRAL crater
    if (n.owner === 'neutral' && n.claimProgress > 0) {
      const frac = Math.max(0, Math.min(1, n.claimProgress / CAPTURE.establishSeconds));
      const ar = bodyR + 12;
      g.lineWidth = 3;
      g.strokeStyle = 'rgba(120,255,180,0.85)';
      g.setLineDash([6, 5]);
      g.beginPath();
      g.arc(0, 0, ar, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
      g.stroke();
      g.setLineDash([]);
    }

    g.restore();
  }

  // ------------------------------------------------------------------ turrets
  function drawTurret(t: Turret, time: number): void {
    const alive = t.hp > 0;
    const building = t.timer > 0;
    const col = alive ? SIDE_COLOR[t.side] : NEUTRAL;
    const dark = SIDE_DARK[t.side];
    const bodyR = TURRET.radius;
    g.save();
    g.translate(t.pos.x, t.pos.y);

    // poison-cloud aura (only while active) — a soft toxic ring
    if (alive && !building) {
      g.save();
      g.globalAlpha = 0.10 + 0.04 * Math.sin(time * 3);
      g.fillStyle = '#9ad84f';
      g.beginPath();
      g.arc(0, 0, TURRET.auraRadius, 0, TAU);
      g.fill();
      g.restore();
    }

    // body: a squat hexagon (structure), dimmed while inert (building/repairing) or rubble.
    g.globalAlpha = building ? 0.5 : alive ? 1 : 0.4;
    g.beginPath();
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * TAU + Math.PI / 6;
      const px = Math.cos(a) * bodyR;
      const py = Math.sin(a) * bodyR;
      if (i === 0) g.moveTo(px, py); else g.lineTo(px, py);
    }
    g.closePath();
    const grad = g.createRadialGradient(0, 0, bodyR * 0.2, 0, 0, bodyR);
    grad.addColorStop(0, 'rgba(20,30,36,0.92)');
    grad.addColorStop(1, alive ? dark : 'rgba(40,48,54,0.85)');
    g.fillStyle = grad;
    g.fill();
    g.lineWidth = 2;
    g.strokeStyle = col;
    g.stroke();
    g.globalAlpha = 1;

    // barrel tick (a small centred cross) so it reads as a turret, not a node
    if (alive) {
      g.strokeStyle = col;
      g.lineWidth = 2;
      g.beginPath();
      g.moveTo(-bodyR * 0.45, 0); g.lineTo(bodyR * 0.45, 0);
      g.moveTo(0, -bodyR * 0.45); g.lineTo(0, bodyR * 0.45);
      g.stroke();
    }

    // HP ring (only when hurt) — mirrors the node integrity arc.
    const hpFrac = t.maxHp > 0 ? Math.max(0, Math.min(1, t.hp / t.maxHp)) : 1;
    if (alive && hpFrac < 0.999) {
      const ar = bodyR + 6;
      g.lineWidth = 3;
      g.strokeStyle = 'rgba(255,255,255,0.12)';
      g.beginPath();
      g.arc(0, 0, ar, 0, TAU);
      g.stroke();
      g.strokeStyle = hpFrac > 0.5 ? col : hpFrac > 0.25 ? '#ffcf5c' : '#ff5a5a';
      g.beginPath();
      g.arc(0, 0, ar, -Math.PI / 2, -Math.PI / 2 + hpFrac * TAU);
      g.stroke();
    }

    // build/repair progress — a dashed arc filling as the timer runs down.
    if (building) {
      const frac = Math.max(0, Math.min(1, 1 - t.timer / TURRET.buildSeconds));
      const ar = bodyR + 10;
      g.lineWidth = 3;
      g.strokeStyle = 'rgba(120,255,180,0.85)';
      g.setLineDash([6, 5]);
      g.beginPath();
      g.arc(0, 0, ar, -Math.PI / 2, -Math.PI / 2 + frac * TAU);
      g.stroke();
      g.setLineDash([]);
    }

    g.restore();
  }

  // ------------------------------------------------------------------ units
  function unitSilhouette(u: Unit, r: number, ang: number): void {
    g.beginPath();
    // A crowned Queen (a drone) gets a regal rounded-diamond body instead of the drone chevron.
    if (u.queen) {
      g.moveTo(0, -r * 1.25);
      g.quadraticCurveTo(r * 1.15, -r * 0.2, r * 0.5, r);
      g.quadraticCurveTo(0, r * 1.3, -r * 0.5, r);
      g.quadraticCurveTo(-r * 1.15, -r * 0.2, 0, -r * 1.25);
      g.closePath();
      return;
    }
    switch (u.kind) {
      case 'lancer': {
        // slim dart pointing along travel
        g.rotate(ang);
        g.moveTo(r * 1.4, 0);
        g.lineTo(-r, r * 0.7);
        g.lineTo(-r * 0.5, 0);
        g.lineTo(-r, -r * 0.7);
        g.closePath();
        break;
      }
      case 'phalanx': {
        // broad shielded hexagon
        for (let i = 0; i < 6; i++) {
          const a = (i / 6) * TAU;
          const px = Math.cos(a) * r * 1.15;
          const py = Math.sin(a) * r;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
      case 'stinger': {
        // ranged diamond
        g.moveTo(0, -r * 1.2);
        g.lineTo(r, 0);
        g.lineTo(0, r * 1.2);
        g.lineTo(-r, 0);
        g.closePath();
        break;
      }
      case 'engulfer': {
        // big amoeboid blob (lobed)
        const N = 10;
        for (let i = 0; i <= N; i++) {
          const a = (i / N) * TAU;
          const rr = r * (1.15 + 0.12 * Math.sin(a * 3));
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
      case 'toxifer': {
        // spiny circle
        const pts = 7;
        for (let k = 0; k < pts * 2; k++) {
          const rr = k % 2 === 0 ? r * 1.2 : r * 0.7;
          const a = (Math.PI * k) / pts;
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (k === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
      case 'drone': {
        // sleek forward-swept chevron dart (fastest thing on the map)
        g.rotate(ang);
        g.moveTo(r * 1.5, 0);
        g.lineTo(-r * 0.4, r * 0.85);
        g.lineTo(-r * 0.9, r * 0.35);
        g.lineTo(-r * 0.2, 0);
        g.lineTo(-r * 0.9, -r * 0.35);
        g.lineTo(-r * 0.4, -r * 0.85);
        g.closePath();
        break;
      }
      case 'healer': {
        // rounded cross / plus cell
        const t = r * 0.42;
        const o = r * 1.15;
        g.moveTo(-t, -o);
        g.lineTo(t, -o);
        g.lineTo(t, -t);
        g.lineTo(o, -t);
        g.lineTo(o, t);
        g.lineTo(t, t);
        g.lineTo(t, o);
        g.lineTo(-t, o);
        g.lineTo(-t, t);
        g.lineTo(-o, t);
        g.lineTo(-o, -t);
        g.lineTo(-t, -t);
        g.closePath();
        break;
      }
      case 'grazer': {
        // big lumbering lobed beast (bigger, rounder amoeboid than the engulfer)
        const N = 14;
        for (let i = 0; i <= N; i++) {
          const a = (i / N) * TAU;
          const rr = r * (1.05 + 0.14 * Math.sin(a * 4) + 0.05 * Math.cos(a * 2));
          const px = Math.cos(a) * rr;
          const py = Math.sin(a) * rr;
          if (i === 0) g.moveTo(px, py);
          else g.lineTo(px, py);
        }
        g.closePath();
        break;
      }
    }
  }

  // A little three-point crown, centered — the Queen's badge (drawn in the dark unit-icon ink).
  function drawQueenCrown(r: number): void {
    const w = r * 0.62; // half-width
    const top = -r * 0.34; // crown tip line
    const base = r * 0.28; // crown base line
    g.save();
    g.fillStyle = 'rgba(10,16,20,0.92)';
    g.beginPath();
    g.moveTo(-w, base);
    g.lineTo(-w, top);
    g.lineTo(-w * 0.5, base * 0.25);
    g.lineTo(0, top - r * 0.16);
    g.lineTo(w * 0.5, base * 0.25);
    g.lineTo(w, top);
    g.lineTo(w, base);
    g.closePath();
    g.fill();
    g.restore();
  }

  function drawUnit(u: Unit, alpha: number, selected: boolean, focused: boolean): void {
    // Extrapolate along velocity by the render alpha for smooth motion between sim steps.
    const x = u.pos.x + u.vel.x * alpha * RENDER_STEP;
    const y = u.pos.y + u.vel.y * alpha * RENDER_STEP;
    const r = u.radius;
    const ang = Math.atan2(u.vel.y, u.vel.x);
    const col = allyColor(u.side);

    g.save();
    g.translate(x, y);

    // selection ring (world space)
    if (selected) {
      g.strokeStyle = '#ffffff';
      g.lineWidth = 2;
      g.setLineDash([4, 4]);
      g.beginPath();
      g.arc(0, 0, r + 6, 0, TAU);
      g.stroke();
      g.setLineDash([]);
    }

    // body
    g.save();
    unitSilhouette(u, r, ang);
    g.fillStyle = col;
    g.globalAlpha = 0.92;
    g.fill();
    g.globalAlpha = 1;
    g.lineWidth = 1.5;
    g.strokeStyle = allyDark(u.side);
    g.stroke();
    g.restore();

    // DoT indicator — stacking toxifer poison
    if (u.dotStacks > 0) {
      g.fillStyle = 'rgba(190,90,220,0.9)';
      g.beginPath();
      g.arc(r * 0.6, -r * 0.6, 2.5, 0, TAU);
      g.fill();
    }

    // Base-defense poison — sickly green wash + rising bubbles; envenomed (stacks>1) burns brighter.
    if (u.poisonTimer > 0) {
      const envenomed = u.poisonStacks > 1;
      g.save();
      unitSilhouette(u, r, ang);
      g.fillStyle = envenomed ? 'rgba(150,255,90,0.4)' : 'rgba(120,220,110,0.28)';
      g.fill();
      g.restore();
      const bubbles = envenomed ? Math.min(4, u.poisonStacks) : 2;
      g.fillStyle = envenomed ? 'rgba(190,255,120,0.95)' : 'rgba(150,230,140,0.9)';
      const drift = performance.now() / 1000;
      for (let i = 0; i < bubbles; i++) {
        const bx = Math.cos(i * 2.4 + u.pos.x * 0.05) * r * 0.6;
        const by = -((drift * 22 + i * 20 + u.pos.y) % (r * 2)) + r * 0.4;
        g.beginPath();
        g.arc(bx, by, 1.6, 0, TAU);
        g.fill();
      }
    }

    // role icon (custom canvas pictograph) — the Queen wears a crown instead of the drone eye
    if (u.queen) {
      drawQueenCrown(r);
    } else {
      drawUnitIcon(g, u.kind, r * 0.92, 'rgba(10,16,20,0.92)');
    }

    // hp pip (only when hurt)
    const hpFrac = u.maxHp > 0 ? Math.max(0, Math.min(1, u.hp / u.maxHp)) : 1;
    if (hpFrac < 0.999) {
      const bw = r * 2;
      const bh = 3;
      const by = r + 5;
      g.fillStyle = 'rgba(0,0,0,0.6)';
      g.fillRect(-bw / 2, by, bw, bh);
      g.fillStyle = hpFrac > 0.5 ? '#6bd66b' : hpFrac > 0.25 ? '#ffcf5c' : '#ff5a5a';
      g.fillRect(-bw / 2, by, bw * hpFrac, bh);
    }

    // focus reticle — this enemy is being focus-fired by a selected unit
    if (focused) {
      const rr = r + 9;
      g.strokeStyle = '#ff5a5a';
      g.lineWidth = 2;
      for (let i = 0; i < 4; i++) {
        const a = (i / 4) * TAU + Math.PI / 4;
        const x1 = Math.cos(a) * rr;
        const y1 = Math.sin(a) * rr;
        const x2 = Math.cos(a) * (rr + 5);
        const y2 = Math.sin(a) * (rr + 5);
        g.beginPath();
        g.moveTo(x1, y1);
        g.lineTo(x2, y2);
        g.stroke();
      }
      g.beginPath();
      g.arc(0, 0, rr, 0, TAU);
      g.globalAlpha = 0.5;
      g.stroke();
      g.globalAlpha = 1;
    }

    g.restore();
  }

  // ------------------------------------------------------------------ oxygenation wash
  function drawOxyWash(): void {
    const c = sim.state.clocks;
    if (!c.oxyActive) return;
    const level = Math.max(0, Math.min(1, c.oxyLevel));
    const a = 0.1 + 0.25 * level;
    const grad = g.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, `rgba(190,235,255,${a})`);
    grad.addColorStop(1, `rgba(120,180,255,${a * 0.4})`);
    g.fillStyle = grad;
    g.fillRect(0, 0, W, H);
    // rising O2 motes (screen space, deterministic drift)
    const t = performance.now() / 1000;
    g.fillStyle = `rgba(230,250,255,${0.25 + 0.4 * level})`;
    const motes = 24;
    for (let i = 0; i < motes; i++) {
      const mx = ((i * 97.13) % W);
      const speed = 30 + (i % 5) * 14;
      const my = H - ((t * speed + i * 53) % (H + 40));
      const rr = 1.5 + (i % 3);
      g.beginPath();
      g.arc(mx, my, rr, 0, TAU);
      g.fill();
    }
  }

  // ------------------------------------------------------------------ frame
  const minimapRect: MinimapRect = { x: 0, y: 0, w: 0, h: 0 };
  let hitSig = ''; // last-applied hit-div geometry, so we skip redundant DOM writes each frame
  function layoutMinimap(): void {
    // RIGHT-edge ribbon. Widen slightly (world is a tall 1:2.19 strip; a wider ribbon lets the
    // camera-viewport "control box" read as a real rectangle instead of a squished sliver). The
    // dock insets the joystick/FAB left of this column so the z-60 overlay never paints over them.
    const ribbonW = Math.max(28, Math.min(40, W * 0.09));
    minimapRect.x = W - ribbonW - 8;
    // Start below the back button (safe-area-top + 42px) and the centered glyph pill (≈ +48px),
    // scaling with the measured inset so Dynamic-Island phones (~59px) don't clip the ribbon top;
    // on non-notched screens this drops to 60 for maximum length. The ribbon renders on the overlay
    // canvas (above the dock), so it runs the FULL height down to the physical bottom edge.
    const top = Math.max(60, safeTop + 56);
    minimapRect.y = top;
    minimapRect.w = ribbonW;
    minimapRect.h = H - top - 6;

    // NODE PANEL OPEN: shrink the ribbon so its bottom sits just ABOVE the panel's top edge, keeping the
    // whole map in view without ever covering the node controls (the overlay can't be put behind the
    // panel — see the stacking-context note where panelOpen is declared). Measured live so the ribbon
    // tracks the panel as its content (and height) changes.
    if (panelOpen) {
      if (!dockEl) dockEl = document.getElementById('hud-dock');
      const dockTop = dockEl ? dockEl.getBoundingClientRect().top : H;
      const maxBottom = dockTop - 8; // 8px breathing room above the panel
      minimapRect.h = Math.max(0, Math.min(minimapRect.h, maxBottom - minimapRect.y));
    }

    // Keep the interactive hit div glued to the ribbon; only touch the DOM when the rect changes
    // (it changes on resize, not per frame), so this stays off the render hot path.
    const sig = `${minimapRect.x}|${minimapRect.y}|${minimapRect.w}|${minimapRect.h}`;
    if (sig !== hitSig) {
      hitSig = sig;
      minimapHit.style.left = `${minimapRect.x}px`;
      minimapHit.style.top = `${minimapRect.y}px`;
      minimapHit.style.width = `${minimapRect.w}px`;
      minimapHit.style.height = `${minimapRect.h}px`;
    }
  }

  function render(alpha: number): void {
    if (W === 0 || H === 0) return;
    const now = performance.now();
    let dt = (now - last) / 1000;
    last = now;
    if (!(dt > 0)) dt = 0;
    if (dt > 0.05) dt = 0.05;
    cam.update(dt);

    const state = sim.state;
    const time = now / 1000;

    // reset to device pixels; author everything below in CSS px until the world transform.
    g.setTransform(dpr, 0, 0, dpr, 0, 0);

    // 1. water (screen space)
    drawWater();

    // enter world space
    g.save();
    g.translate(W / 2, H / 2);
    g.scale(cam.zoom, cam.zoom);
    g.translate(-cam.pos.x, -cam.pos.y);

    // 2. atmosphere
    drawAtmosphere();

    // fog: recompute this frame's visibility from your units + owned nodes
    fog.update(state);

    // 3. nodes — only ones you've SCOUTED (or own). An unexplored node isn't drawn at all, so you
    //    can't discover it just by panning the camera over the fog; once seen it stays remembered
    //    (the veil then dims it while it's out of sight).
    for (const n of state.nodes) {
      if (n.owner !== viewSide && !fog.isExplored(n.pos)) continue;
      drawNode(n, time);
    }

    // fog veil — over terrain/nodes; your own units + their vision punch soft holes in it
    fog.paint(g);

    // 3b. turrets — your own always; enemy turrets only where currently in vision. Under units (they
    //     draw on top), over the veil (fog can dim them), like structures.
    for (const t of state.turrets) {
      if (t.side !== viewSide && !fog.isVisible(t.pos)) continue;
      drawTurret(t, time);
    }

    // 4. units — YOUR units always; rival + wild units only where currently in vision
    const selected = new Set(state.selectionBySide[viewSide]);
    const focusTargets = new Set<string>();
    for (const id of state.selectionBySide[viewSide]) {
      const su = sim.unitById(id);
      if (su && su.focusId) focusTargets.add(su.focusId);
    }
    for (const u of state.units) {
      if (u.side !== viewSide && !fog.isVisible(u.pos)) continue;
      drawUnit(u, alpha, selected.has(u.id), focusTargets.has(u.id));
    }

    // Ranged-defense tracers (fading over their ttl). Hidden if the firing node is in the fog —
    // otherwise a shot from an unexplored enemy base would leak its position.
    for (const s of state.shots) {
      if (s.side !== viewSide && !fog.isVisible(s.from)) continue;
      const a = Math.max(0, Math.min(1, s.ttl / DEF.shotTtl));
      g.strokeStyle = SIDE_COLOR[s.side];
      g.globalAlpha = 0.85 * a;
      g.lineWidth = 2.5;
      g.beginPath();
      g.moveTo(s.from.x, s.from.y);
      g.lineTo(s.to.x, s.to.y);
      g.stroke();
      // bright impact dot at the target end
      g.fillStyle = '#ffffff';
      g.globalAlpha = a;
      g.beginPath();
      g.arc(s.to.x, s.to.y, 3, 0, TAU);
      g.fill();
      g.globalAlpha = 1;
    }

    g.restore();

    // 5. Great Oxygenation wash (screen space)
    drawOxyWash();

    // 5b. transient order pings (move / attack) — screen space, above the world, below the minimap.
    drawPings(now);

    // 6. minimap — drawn on the OVERLAY canvas (above the dock) so its full height stays visible.
    //    Suppressed while the control panel is open (normal-play overlay only).
    if (minimapVisible) {
      layoutMinimap();
      og.setTransform(dpr, 0, 0, dpr, 0, 0);
      og.clearRect(0, 0, W, H);
      drawMinimap(og, state, cam, minimapRect, fog, viewSide);
    }
  }

  // Tear down every DOM node + window listener this mount created, so a fresh mount (e.g. the
  // post-game replay) can own the screen cleanly with no leaked overlays or stale resize handlers.
  function destroy(): void {
    destroyed = true;
    window.removeEventListener('resize', resize);
    window.visualViewport?.removeEventListener('resize', resize);
    window.removeEventListener('load', resize);
    window.removeEventListener('orientationchange', onOrient);
    window.removeEventListener('microbe:panel', onPanelEvt);
    window.removeEventListener('microbe:ping', onPingEvt);
    overlay.remove();
    minimapHit.remove();
    satProbe.remove();
  }

  return { render, cam, minimapHit, enableGodView, destroy };
}
